import { Injectable } from '@angular/core';
import {
    BaseHttpService,
    GetById,
    Update,
    UriBuilderParams
} from '@app/core/services/base/base-http.service';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import {
    AdvancedCredentials,
    BasicCredentials, CredentialGroup,
    Credentials,
    CredentialsType
} from '@app/shared/models/credentials';
import { map } from 'rxjs/operators';
import { DRResponse } from '@app/shared/models/response';

export class CredentialsUriBuilderParams extends UriBuilderParams<Credentials> {

    constructor(base: Credentials | CredentialsType) {
        super(base);
    }

    public buildUri(): string {
        return `/${this.base}`;
    }
}

@Injectable({ providedIn: 'root' })
export class CredentialsService extends BaseHttpService<Credentials, CredentialsUriBuilderParams>
                                implements GetById<Credentials, CredentialsUriBuilderParams>,
                                           Update<Credentials, CredentialsUriBuilderParams> {

    constructor(httpServiceBundle: HttpServiceBundle) {
        super(Credentials, httpServiceBundle, true);
        // Setting a new uri because this is not an SMP call
        this.uri += `/${BackendUriUnits.CLIENTS}/${BackendUriUnits.PREFERENCES}/${BackendUriUnits.CREDENTIALS}`;
    }


    // BASE METHODS ****************************************************************************************

    public getById(uriParams: CredentialsUriBuilderParams): Observable<Credentials> {
        const uri = this.constructUri(uriParams);
        const queryParams: any = { completeGroupCredentials: true };

        return this.http.get<DRResponse<Credentials>>(uri, { params: queryParams })
                        .pipe(map(res => {
                            res.data.credentialsType = uriParams.base as CredentialsType;
                            return new Credentials(res.data);
                        }));
    }

    public update(uriParams: CredentialsUriBuilderParams, credentialsToUpdate: Credentials): Observable<Credentials> {
        const uri = this.constructUri(uriParams);
        return this.http.put<DRResponse<Credentials>>(uri, credentialsToUpdate.getInUpdateFormat())
                        .pipe(map(res => {
                            res.data.credentialsType = uriParams.base as CredentialsType;
                            return new Credentials(res.data);
                        }));
    }


    // ADVANCED CREDENTIALS

    public updateAdvancedCredentials(uriParams: CredentialsUriBuilderParams, credentialsToUpdate: AdvancedCredentials): Observable<AdvancedCredentials> {
        const uri = `${this.constructUri(uriParams)}/${credentialsToUpdate.assignment}/${credentialsToUpdate.id}`;
        return this.http.put<DRResponse<AdvancedCredentials>>(uri, credentialsToUpdate)
                        .pipe(map(res => (<any>Credentials.generateCredentialsBasedOnType(res.data, uriParams.base as CredentialsType, credentialsToUpdate.assignment) as AdvancedCredentials)));
    }


    // TEST EMAIL

    public sendTestEmail(credentials: BasicCredentials): Observable<DRResponse<any>> {
        const advancedUriPart = BasicCredentials.isAdvancedCredentials(credentials) ? `/${credentials.assignment}/${credentials.id}` : ``;
        const uri = `${this.uri}/email${advancedUriPart}/test`;
        return this.http.post<DRResponse<any>>(uri, credentials)
                        .pipe(res => res);
    }

}
